import React, { useState } from "react";
import {
  Box,
  VStack,
  Text,
  IconButton,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";
import { FaMicrophone, FaCamera, FaPen, FaEye } from "react-icons/fa";
import PrimaryButton from "../../../components/PrimaryButton";
import DoctorOpinionModal from "./DoctorOpinionModal";

const ActionButton = ({ icon, label, onClick, hasData }) => {
  return (
    <Box position="relative">
      {hasData ? (
        <PrimaryButton size="lg" leftIcon={<FaEye />} onClick={onClick}>
          View
        </PrimaryButton>
      ) : (
        <IconButton
          icon={icon}
          onClick={onClick}
          aria-label={label}
          size="lg"
          borderRadius="md"
          isDisabled={!hasData}
          opacity={0.5}
        />
      )}
    </Box>
  );
};

const MedicalOpinion = ({ doctorProvidedOpinion, onShareOpinion }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedOpinion, setSelectedOpinion] = useState(null);

  const handleOpinionClick = (type) => {
    const searchType = type === "Treatment Plan" ? "TreatmentPlan" : type;

    // Find the matching opinion from the opinion array
    const opinionData = doctorProvidedOpinion?.opinion?.find(
      (item) => item.type === searchType
    );
    setSelectedOpinion(opinionData);
    onOpen();
  };

  const getOpinionByTypeAndMime = (type, mimeType) => {
    const searchType = type === "Treatment Plan" ? "TreatmentPlan" : type;

    // Check if opinion array exists in doctorProvidedOpinion
    if (!doctorProvidedOpinion?.opinion) return false;

    // Find the matching opinion type from the opinion array
    const opinionData = doctorProvidedOpinion.opinion.find(
      (item) => item.type === searchType
    );

    if (!opinionData) return false;

    // Check if the mime type matches and opinion array has data
    return (
      opinionData.mime_type === mimeType && opinionData.opinion?.length > 0
    );
  };

  return (
    <Box p={6} mx="auto" bg="white">
      <VStack spacing={8} align="stretch">
        {/* Diagnosis Section */}
        <Box p={6} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              Diagnosis
            </Text>
            <Flex gap={6}>
              <ActionButton
                icon={<FaCamera />}
                hasData={getOpinionByTypeAndMime("Diagnosis", "Image")}
                onClick={() => handleOpinionClick("Diagnosis")}
              />
              <ActionButton
                icon={<FaPen />}
                hasData={getOpinionByTypeAndMime("Diagnosis", "Text")}
                onClick={() => handleOpinionClick("Diagnosis")}
              />
              <ActionButton
                icon={<FaMicrophone />}
                hasData={getOpinionByTypeAndMime("Diagnosis", "Audio")}
                onClick={() => handleOpinionClick("Diagnosis")}
              />
            </Flex>
          </Flex>
        </Box>

        {/* Treatment Plan Section */}
        <Box p={6} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              Treatment Plan
            </Text>
            <Flex gap={6}>
              <ActionButton
                icon={<FaCamera />}
                hasData={getOpinionByTypeAndMime("Treatment Plan", "Image")}
                onClick={() => handleOpinionClick("Treatment Plan")}
              />
              <ActionButton
                icon={<FaPen />}
                hasData={getOpinionByTypeAndMime("Treatment Plan", "Text")}
                onClick={() => handleOpinionClick("Treatment Plan")}
              />
              <ActionButton
                icon={<FaMicrophone />}
                hasData={getOpinionByTypeAndMime("Treatment Plan", "Audio")}
                onClick={() => handleOpinionClick("Treatment Plan")}
              />
            </Flex>
          </Flex>
        </Box>

        {/* Description Section */}
        <Box p={6} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              Description
            </Text>
            <Flex gap={6}>
              <ActionButton
                icon={<FaCamera />}
                hasData={getOpinionByTypeAndMime("Description", "Image")}
                onClick={() => handleOpinionClick("Description")}
              />
              <ActionButton
                icon={<FaPen />}
                hasData={getOpinionByTypeAndMime("Description", "Text")}
                onClick={() => handleOpinionClick("Description")}
              />
              <ActionButton
                icon={<FaMicrophone />}
                hasData={getOpinionByTypeAndMime("Description", "Audio")}
                onClick={() => handleOpinionClick("Description")}
              />
            </Flex>
          </Flex>
        </Box>

        {/* Share Opinion Button */}
        <Flex justify="flex-end" mt={4}>
          <PrimaryButton
            size="lg"
            py={7}
            fontSize="md"
            width={{ base: "100%", md: "auto" }}
            onClick={onShareOpinion}
          >
            Share Opinion
          </PrimaryButton>
        </Flex>

        <DoctorOpinionModal
          isOpen={isOpen}
          onClose={onClose}
          opinionData={selectedOpinion}
        />
      </VStack>
    </Box>
  );
};

export default MedicalOpinion;
